// filepath: c:\Users\gcodron\work\front3\app\components\ToolsBar\ProcessingListButton.tsx
import React, { memo, useMemo } from 'react';
import * as _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import './ProcessingListButton.scss';
import { Dropdown, Icon } from 'semantic-ui-react';
import { Toggle } from 'rsuite';
import { useSelector } from 'react-redux';

import ToolsDropdownIndicator from './ToolsDropdownIndicator';
import { selectAiOnlyConfiguration } from 'app/redux/aiOnlyConfiguration/reducer';
import { selectProcessingFineTuningEnabled } from 'app/redux/viewerConfiguration/reducer';
import ProcessingListItem from 'app/components/ToolsBar/ProcessingListItem';

export interface ProcessingItem {
  key: string;
  label: string;
  callback: (study: any) => void;
  isCurrentProcessing?: boolean;
  onEditClick?: () => void;
}

export type ProcessingDropdownButtonProps = {
  isShown?: boolean;
  study: any;
  processingList?: { [key: string]: ProcessingItem };
  isFineTuning?: boolean;
  onFineTuningClick?: (isEnabled: boolean) => void;
  isGridEnabled?: boolean;
  onToggleGrid?: () => void;
};

/**
 * Window Width Window Center Group gather to tools linked to contrast with the active tool
 * being displayed and the options tools and function in a dropdown menu.
 */
const ProcessingDropdownButton = memo(
  ({
    processingList,
    isShown,
    study,
    isFineTuning,
    onFineTuningClick,
    isGridEnabled,
    onToggleGrid,
  }: ProcessingDropdownButtonProps) => {
    const aiOnlyConfiguration = useSelector(selectAiOnlyConfiguration);
    const isProcessingFineTuningEnabled = useSelector(selectProcessingFineTuningEnabled);
    const intl = useIntl();
    const aiOnlyEnabled = aiOnlyConfiguration.enabled;

    const options = useMemo(
      () =>
        _.map(processingList, ({ key, label, callback, isCurrentProcessing, onEditClick }) => ({
          key,
          text: label,
          onClick: () => callback(study),
          selected: isCurrentProcessing,
          onEditClick,
        })),
      [processingList, study]
    );

    const gridItemProps = useMemo(
      () => ({
        className: 'picoxia grid-toggle-item',
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleGrid?.();
        },
      }),
      [onToggleGrid]
    );

    if (!isShown || aiOnlyEnabled) return null;

    return (
      <div
        title={intl.formatMessage({ id: 'tools.processing.tooltip' })}
        className="processing-list-tool"
      >
        <Dropdown
          trigger={
            <button className="picoxia processing-list-button" type="button">
              <div className="tool-button">
                <i className="svg-icon processing" />
              </div>
              <ToolsDropdownIndicator />
            </button>
          }
          icon={false}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Dropdown.Menu className="processing-list-dropdown">
            <Dropdown.Header>
              <i className="svg-icon processing" />
              <FormattedMessage id="tools.processing.title" />
            </Dropdown.Header>
            {onToggleGrid && (
              <Dropdown.Item
                as="div"
                closeOnChange={false}
                title={intl.formatMessage({ id: 'tools.processing.grid.toggle.tooltip' })}
                {...gridItemProps}
              >
                <Toggle
                  checked={!!isGridEnabled}
                  size="md"
                  checkedChildren={<FormattedMessage id="general.with" />}
                  unCheckedChildren={<FormattedMessage id="general.without" />}
                >
                  <FormattedMessage id="tools.processing.grid.toggle.label" />
                </Toggle>
              </Dropdown.Item>
            )}
            <Dropdown.Menu scrolling>
              {options.map(({ key, ...option }) => (
                <ProcessingListItem
                  key={key}
                  {...option}
                  isEditEnabled={isProcessingFineTuningEnabled}
                  className="processing-list-dropdown__item"
                />
              ))}
            </Dropdown.Menu>
            {isProcessingFineTuningEnabled && (
              <Dropdown.Item
                as="button"
                className="picoxia fine-tuning-button"
                selected={isFineTuning}
                onClick={() => onFineTuningClick(!isFineTuning)}
              >
                <Icon name="plus" />
                <FormattedMessage id="tools.processing.finetuning.add" />
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }
);

export default ProcessingDropdownButton;
